// The only place this module talks to the Competitor Benchmark API.
//
// Unlike Profit Yield, this backend already existed: Meteoric exposes
// `/api/competitor-benchmark/{data,freshness,set,variants,collect}`, which is
// why this module is a pure UI port with no backend work. The Pages Function
// proxy points at Meteoric today; when the benchmark service moves off it, the
// change is one origin variable and nothing in this file.

export type FreshnessMeta = {
  data_as_of: string | null;
  source: string | null;
  expected_max_age_hours: number | null;
};

export type Metric = "price" | "bsr";
export type FreshnessStatus = "fresh" | "stale" | "critical" | "no_data";

export type SeriesPoint = {
  obs_date: string;
  median: number | null;
  avg: number | null;
  max: number | null;
  min: number | null;
  max_brand: string | null;
  min_brand: string | null;
  /** How many competitors were observed that day. A thin day is not a trend. */
  n: number;
};

export type MetricBlock = {
  series: SeriesPoint[];
  last_collected_at: string | null;
  /**
   * The producer's own verdict on recency. The UI maps the status to a render
   * treatment and NEVER re-derives the thresholds: one definition, upstream.
   */
  freshness_status: FreshnessStatus;
};

export type VariantEntry = {
  product: string | null;
  variant: string | null;
  price: MetricBlock;
  bsr: MetricBlock;
};

export type BenchmarkPayload = {
  marketplace: string;
  computed_at: string;
  variants: Record<string, VariantEntry>;
};

/** The API answers with this instead of a payload before the first collection run. */
export type PendingPayload = { pending: true };

export type SetEntry = {
  entry_id: string;
  variant_id: string;
  marketplace: string;
  competitor_brand: string | null;
  competitor_product: string | null;
  competitor_asin: string;
  size_metric: string | null;
  quantity: number | null;
  channel: string | null;
  /** Our own listing, carried in the set but excluded from the aggregates. */
  is_self: boolean;
  status: "active" | "archived";
  added_at: string | null;
  archived_at: string | null;
};

export type VariantRow = { variant_id: string; product: string | null; variant: string | null };

export type MarketFreshness = {
  marketplace: string;
  last_collected_at: string | null;
  freshness_status: FreshnessStatus;
};

/** Canonical marketplace vocabulary, in UI order. UK, never GB. */
export const MARKETPLACES = [
  "UK", "DE", "FR", "ES", "IT", "NL", "BE", "PL", "IE", "SE", "AE", "SA", "US",
] as const;

export type Marketplace = (typeof MARKETPLACES)[number];

export function isMarketplace(code: string): code is Marketplace {
  return (MARKETPLACES as readonly string[]).includes(code.toUpperCase());
}

// Same-origin via the Pages Function proxy — the routing decision that
// everything goes through os.ketosource.co (Damien, 2026-09-02).
const BASE = import.meta.env.VITE_BENCHMARK_API_BASE ?? "/meteoric-competitor-benchmark/v1";

export class BenchmarkError extends Error {
  constructor(
    readonly status: number,
    readonly body: unknown,
  ) {
    super(`Competitor Benchmark API ${status}`);
  }
}

async function safeBody(response: Response): Promise<unknown> {
  try {
    return await response.json();
  } catch {
    return null;
  }
}

async function get<T>(path: string): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    credentials: "include",
    headers: { Accept: "application/json" },
  });
  if (!response.ok) throw new BenchmarkError(response.status, await safeBody(response));
  return (await response.json()) as T;
}

async function post<T>(path: string, body?: unknown): Promise<T> {
  const response = await fetch(`${BASE}${path}`, {
    method: "POST",
    credentials: "include",
    headers: { Accept: "application/json", "Content-Type": "application/json" },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  if (!response.ok) throw new BenchmarkError(response.status, await safeBody(response));
  return (await response.json().catch(() => ({}))) as T;
}

export const getBenchmarkData = (marketplace: Marketplace) =>
  get<BenchmarkPayload | PendingPayload>(`/data?marketplace=${encodeURIComponent(marketplace)}`);

export const getMarketFreshness = () =>
  get<{ marketplaces?: MarketFreshness[] }>("/freshness").then((b) => b.marketplaces ?? []);

export const getVariants = () =>
  get<{ variants?: VariantRow[] }>("/variants").then((b) => b.variants ?? []);

export const getSet = (includeArchived: boolean) =>
  get<{ entries?: SetEntry[] }>(`/set${includeArchived ? "?include_archived=1" : ""}`).then(
    (b) => b.entries ?? [],
  );

export type NewSetEntry = {
  variant_id: string;
  marketplace: string;
  competitor_asin: string;
  is_self: boolean;
  competitor_brand?: string;
  competitor_product?: string;
  size_metric?: string;
  quantity?: number;
};

export const addSetEntries = (entries: NewSetEntry[]) => post<unknown>("/set", { entries });

/**
 * Archive or restore, never delete. Archiving keeps the history and every past
 * aggregate the entry contributed to — a deleted competitor would silently
 * rewrite months of medians.
 */
export const setEntryLifecycle = (entryId: string, action: "archive" | "restore") =>
  post<unknown>(`/set/${encodeURIComponent(entryId)}/${action}`);

export const triggerCollection = () => post<unknown>("/collect");

export function isPending(payload: BenchmarkPayload | PendingPayload): payload is PendingPayload {
  return (payload as PendingPayload).pending === true;
}

/**
 * The S8 freshness envelope, DERIVED at this boundary rather than asserted by
 * the server.
 *
 * The portal's freshness line is structural (I-6): every module hands
 * `ModuleViewport` a `meta`, and a module that cannot is shown as "we can't
 * confirm this data's date". This backend predates S8 and returns bare payloads,
 * so the date is taken from the WORST `last_collected_at` across the payload —
 * worst, not newest, because one dead marketplace hiding behind a fresh sibling
 * is the exact failure the shell's freshness line exists to surface.
 *
 * This function is a boundary adapter with a shelf life. When the benchmark data
 * moves to a service that emits S8 itself, delete it and read `meta` directly.
 */
export function metaFrom(freshness: MarketFreshness[]): FreshnessMeta {
  const dates = freshness.map((m) => m.last_collected_at).filter((d): d is string => Boolean(d));
  return {
    // No collection anywhere yet is `null`, which the shell renders as an
    // unknown date — not as today, and not as an empty string.
    data_as_of: dates.length === freshness.length && dates.length > 0 ? dates.sort()[0] : null,
    source: "Competitor Benchmark collection",
    expected_max_age_hours: 24,
  };
}

/**
 * One message per API failure. A 404 from `/data` means this marketplace has no
 * published payload yet, which is a different fact from a broken request, and
 * only one of them is worth investigating.
 */
export function messageFor(error: unknown, areaName: string): string {
  if (!(error instanceof BenchmarkError)) {
    return "The benchmark API could not be reached, so nothing is shown. Try again shortly.";
  }
  switch (error.status) {
    case 400:
      return "That marketplace is not one the benchmark collects.";
    case 401:
      return "Your session could not be confirmed. Reload the page to sign in again.";
    case 403:
      return `You do not have permission to change the competitive set. Ask the ${areaName} area owner.`;
    case 404:
      return "Nothing has been published for this marketplace yet.";
    case 409:
      return "That ASIN is already in the set for this variant and marketplace.";
    case 422:
      return "That entry was refused. Check the ASIN is 10 characters and the variant is selected.";
    default:
      return "The benchmark API could not be reached, so nothing is shown. Try again shortly.";
  }
}
